/**
 * /product-spec-template 的中文变体。结构、section、内联 style 与
 * ProductSpecTemplate.tsx 完全一致,仅文案为母语级中文。
 *
 * 目标词:product spec template / technical spec template /
 * feature spec template(产品规格模板 / 技术规格模板 / 功能规格模板)。
 *
 * 差异点:product spec(规格)比 PRD 更靠近实现——它是工程师(或 AI
 * 编码 agent)真正照着开发的文档。本页先真给一份规格模板(Overview /
 * Functional requirements / Technical approach / Data model / API /
 * Edge cases & error states / Dependencies / Test criteria /
 * Rollout plan),让搜索的人拿到能用的东西;再讲规格写在 doc 里会和上游
 * PRD/决策脱节,PRD 一改规格没人同步,于是悄悄漂移;Draftlize 让每节成卡片、
 * 连上游决策,决策变了规格自动标 stale(开发场景叫 drift_detected),
 * 编码 agent 经 MCP 始终读到最新规格。
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function ProductSpecTemplateZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/product-spec-template"
      documentTitle="始终与背后决策同步的产品规格模板 · Draftlize"
      documentDescription="一份可直接复制的产品规格模板(product spec template)——Overview、功能需求、技术方案、数据模型 / API、边界与错误态、依赖、测试标准、发布计划。问题在于:规格一旦写进文档,上游 PRD 一改,它当天就开始漂移。在 Draftlize 里每一节都是一张卡片,连着它所依赖的决策,决策一变,规格自动标 stale——你的编码 agent 永远读到最新那份。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">模板 · 产品规格</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            一份产品规格模板<br/>
            <em>和背后的决策始终同步。</em>
          </h1>
          <p className="lede">
            你是来找一份<em>产品规格模板</em>的——章节、标题、一套能照着开发的结构。它就在下面,拿去用。规格是离实现最近的那份文档:不是"做什么、为什么"(那是 PRD),而是"具体怎么做"。麻烦出在它住在哪里。写进一份独立文档,它就和上游的 <a href="/zh/product-requirements-document" style={{ color: 'var(--amber)' }}>PRD</a>、决策断了联系——于是上面某个决定一改,规格还在描述一个你早已不做的东西,工程师(或 AI agent)照着一份虚构开发。Draftlize 给你同样这八节,把每一节做成一张连着它所依赖决策的卡片,任何一个决策一动,就把规格标为 stale。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">这份模板</span>
            <h2 className="serif">八个章节。<em>复制到任何地方。</em></h2>
          </div>
          <p>这就是整份规格模板。在文档、wiki 或 markdown 里都能用——一份好的功能规格或技术规格本来就是这个形状。原样拿去。下一节讲的是:当每一部分不再是文档里的一个标题、而是一张知道自己依赖什么的卡片时,会发生什么。</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Overview 概述',
              hint: '一屏说清',
              body: '这个功能是什么、面向用户产出什么结果,几行讲完。不写商业理由——那是 PRD 的事——但要让读的人在需求开始前先知道自己要做的是什么。',
            },
            {
              k: '功能需求',
              hint: '行为',
              body: '系统会做什么,写成具体、可验证的行为。"用户在标题为空时提交,显示内联错误并阻止保存。"一条需求若不能变成一个测试用例,它还只是愿望,不是规格。',
            },
            {
              k: '技术方案',
              hint: '怎么做',
              body: '实现的形状:有哪些组件、怎么流转、关键取舍以及为什么选这条而不是那条显而易见的路。规格的价值大半在这里——这是编码 agent 用来避免把已经定过的事重新定一遍的那一节。',
            },
            {
              k: '数据模型 / API',
              hint: '契约',
              body: '表、字段、类型、接口——其它代码要遵守的那层表面。最贵的错都出在这一节,下游又最先把它写死,所以它最需要可被引用,而不是埋在某处。',
            },
            {
              k: '边界与错误态',
              hint: '不happy的路径',
              body: '空、并发、离线、格式错误、被限流、半途失败。demo 和产品之间的差距,全部住在这里——也是当 happy path 的设计一改、没人回头看边界时,最先悄悄烂掉的一节。',
            },
            {
              k: '依赖',
              hint: '它建立在什么之上',
              body: '这份规格假定成立的那些决策、其它规格、服务——定价那个决定、鉴权模型、隔壁两个功能用的那张表。写进文档里,这是一份你会忘记更新的清单。当它上面的世界变动时,正是这一节决定规格还真不真。',
            },
            {
              k: '测试标准',
              hint: '怎样算做完',
              body: '你凭什么知道它能用:哪些用例必须通过、哪些行为必须成立。写在代码之前,这是"上线了"和"上线了,大概吧"之间的那条线。写在代码之后,它就只是在描述代码碰巧做成了什么。',
            },
            {
              k: '发布计划',
              hint: '上线到世界里',
              body: '开关、迁移、灰度、回滚。怎么把它送到用户面前而不毁掉某个夜晚——以及万一出事怎么把它收回来。人人都跳过的那一节,也是 on-call 工程师最希望它当初存在的那一节。',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          这就是模板。下面讲:写在文档里的规格,为什么会从上面的 PRD 漂走——以及我们怎么处理。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">规格为什么会<em>漂移。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">规格住在 PRD 的下一层</h3>
            <p>PRD 说做什么、为什么;规格说具体怎么做。这是一种真实的依赖:只有它要实现的那份 PRD 仍然成立时,这份规格才说得通。但在两份独立文档里,没有任何东西记下这层联系,于是规格自由漂浮——而它离实现越近,错的时候越贵。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">PRD 改了,没人回头同步规格</h3>
            <p>一个决定变了——定价模型、鉴权流程、核心对象上的某个字段。PRD 也许更新了。建立在旧决定上的规格却没有,因为同步它是手工活,还要和发布抢时间。于是数据模型和边界态描述的是一个不再存在的系统,而没有任何横幅提示这件事。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">agent 照着那份过期副本开发</h3>
            <p>把一份文档交给编码 agent,它会精确实现文档所说的——包括三个 commit 之前就悄悄过期的那些部分。一份说不清自己哪几节还可信的规格,比没有规格更糟:它给错误的实现镀上了一层虚假的信心。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一份模板,<em>始终同步。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每一节成为一张卡片,而不是一个标题</h3>
            <p>在 Draftlize 里,规格不是一份大文档——每一部分都是一张有类型的卡片:概述、功能需求、技术方案、数据模型 / API、边界态、依赖、测试标准、发布。结构化、可按 ID 引用,能从一个 thread 或一份姊妹规格里被引,而不是埋在某个 wiki 标题下面。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">一个决策一改,规格自动标 stale</h3>
            <p>因为"依赖"是指向上游决策的真实链接——不是一串名词——其中某个决策一改,建立在它之上的规格卡片就自动变 stale。在开发场景里我们叫它 drift_detected:底座注意到这份实现规格已经和它当初所依据的决定对不上了,就像构建系统让一个被改文件的所有下游全部失效。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的编码 agent 永远读到最新规格</h3>
            <p>Claude Code 或 Cursor,经 MCP,在写下一行代码之前先读这份活的规格——并在同一个表面上看到哪些节被标 stale、为什么。规格不再是一张 agent 盲信的快照,而成为它每一轮都重读、并更新到最近一个决策的上下文。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          PRD 说做什么,规格说怎么做。只有上面的决策仍然成立,照着规格开发才安全——所以,让那些决策在变动时来标记它。
        </blockquote>
        <cite>留下这八节,让底座替你保持它们同步。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '什么是产品规格（spec）？', a: '产品规格是离实现最近的那份文档：不是「做什么、为什么」——那是 PRD——而是「具体怎么做」。它把行为、状态、边缘情况和约束讲到足够细，让工程师或 AI agent 不用猜就能实现。' },
            { q: 'PRD 和 spec 有什么区别？', a: 'PRD 定意图——问题、用户、想要的结果。spec 把意图变成精确的行为。PRD 说「用户可以恢复被删的项目」；spec 讲清恢复具体怎么走、保留多久、要什么权限、每种失败下会怎样。一个决定做什么，另一个定义它怎么工作。' },
            { q: '产品规格模板应该包含什么？', a: '通常是：概述、目标与非目标、详细行为或用户流程、状态与边缘情况、技术约束、依赖、以及待解问题。上面这份模板都覆盖了——直接在文档、wiki 或 markdown 文件里用。' },
            { q: '怎么让 spec 不和上游脱节？', a: '问题在于 spec 会和 PRD、和它上面的决策断了联系，于是上游一改，spec 还在描述一个你早已不做的东西。Draftlize 把每一节接到它所依赖的决策上，任何一个一动就把 spec 标成 stale，让工程师或 agent 永远不会照着一份僵死的旧版开发。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">开始你的规格,<em>注册送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>用多少付多少</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            把上面那份模板拿去用,或者开一个项目,让 agent 从你的 PRD 和决策里把规格起草出来——然后在你开发时,替你把每一节、以及它所依赖的那些决定,一直保持同步。
          </p>
          <a className="btn-primary" href="/">注册送 $5,免费开始</a>
        </div>
      </section>
      <TemplateCta template="product-brief" locale="zh" />
    </MarketingShell>
  );
}
